import { useState } from "react";
import { Box, IconButton } from "@mui/material";
import { Menu } from "lucide-react";
import { Outlet, useLocation, useSearchParams } from "react-router-dom";
import { motion, useReducedMotion } from "motion/react";
import { pageVariants, resolveTransition } from "@/components/Chat/motion";
import { useAuthedApi } from "@/api/useAuthedApi";
import { ChatProvider, type ChatProviderProps } from "@/components/Chat";
import { SessionSidebar } from "@/components/Chat/SessionSidebar";
import { SchedulesSection } from "@/components/Sidebar/SchedulesSection";
import { NotificationsProvider } from "@/notifications/NotificationsProvider";
import { AppearanceSync } from "@/theme/AppearanceSync";
import { AnalyticsProvider } from "@/analytics/AnalyticsProvider";
import { PageScope } from "@/analytics/PageScope";

/**
 * The single shell for every authenticated /workspace route. It lifts
 * NotificationsProvider + ChatProvider + the persistent SessionSidebar above
 * the routed content (<Outlet/>) so navigating between chat and the workspace
 * pages only swaps the content area — the sidebar and chat session stay mounted.
 *
 * The chat route (/workspace) gets a full-bleed column that manages its own
 * scroll + composer; every other route gets the padded, centered inset column
 * with the page-transition motion.
 */
export function WorkspaceLayout() {
  const { projects_enabled, subagents_enabled } = useAuthedApi();
  const apiUrl = import.meta.env.VITE_API_URL ?? "";
  const pathname = useLocation().pathname;
  const reduce = useReducedMotion() ?? false;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [params] = useSearchParams();
  const isChat = pathname === "/workspace";
  // The org-settings page owns its own two-pane layout (sub-sidebar + main),
  // so it needs the full remaining viewport width — no centered 1100px cap.
  const isFullBleedAdmin = pathname.startsWith("/workspace/organization");

  const slots: ChatProviderProps["slots"] = {
    // The flat "hello"/"testt"-style raw project list here was redundant with
    // (and read as visual clutter next to) the per-project chat sections
    // SessionSidebar already renders below Recent chats — removed.
    sidebarTop: <>{subagents_enabled && <SchedulesSection />}</>,
  };

  return (
    <AnalyticsProvider>
      <NotificationsProvider apiUrl={apiUrl}>
        <AppearanceSync />
        <ChatProvider
          apiUrl={apiUrl}
          slots={slots}
          features={{ projects: projects_enabled }}
          // Read once at mount for cold-load deep links; ChatSurface keeps the
          // persistent provider in sync afterwards.
          initialSessionId={params.get("session")}
          initialProjectId={params.get("project_id")}
        >
          <Box
            sx={{
              display: "flex",
              height: "100vh",
              width: "100%",
              overflow: "hidden",
              bgcolor: "transparent",
              color: "text.primary",
            }}
          >
            <SessionSidebar mobileOpen={mobileNavOpen} onMobileClose={() => setMobileNavOpen(false)} />
            {isChat ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  minHeight: 0,
                  position: "relative",
                  bgcolor: "var(--color-canvas)",
                }}
              >
                <Box
                  sx={{
                    display: { xs: "flex", md: "none" },
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <IconButton aria-label="open navigation" onClick={() => setMobileNavOpen(true)} size="small">
                    <Menu size={20} />
                  </IconButton>
                </Box>
                <PageScope>
                  <Outlet />
                </PageScope>
              </Box>
            ) : (
              <Box component="main" sx={{ flex: 1, minWidth: 0, overflowY: "auto", p: { xs: 2, sm: 3 } }}>
                <Box sx={{ maxWidth: isFullBleedAdmin ? "none" : 1100, mx: isFullBleedAdmin ? 0 : "auto", width: "100%" }}>
                  <IconButton
                    aria-label="open navigation"
                    onClick={() => setMobileNavOpen(true)}
                    size="small"
                    sx={{ display: { xs: "inline-flex", md: "none" }, mb: 1 }}
                  >
                    <Menu size={20} />
                  </IconButton>
                  <motion.div
                    key={pathname}
                    initial={reduce ? false : "hidden"}
                    animate="visible"
                    variants={pageVariants}
                    transition={resolveTransition(reduce)}
                  >
                    <PageScope>
                      <Outlet />
                    </PageScope>
                  </motion.div>
                </Box>
              </Box>
            )}
          </Box>
        </ChatProvider>
      </NotificationsProvider>
    </AnalyticsProvider>
  );
}
